import { ConfirmDialog } from '@/components/confirm-dialog';
import { NoteLevelBadge } from '@/components/notes/note-level-badge';
import { NoteLevelPicker } from '@/components/notes/note-level-picker';
import {
  authorName,
  formatNoteDate,
  formatNoteDateTime,
  noteErrorMessage,
} from '@/lib/note-format';
import type { NoteViewer } from '@/lib/notes-queries';
import { cn } from '@/lib/utils';
import { NOTE_BODY_MAX, type Note, type UpdateNoteInput } from '@mss_creator/shared';
import { ActionMenu, Button, TextArea } from '@pms-digital/design-system';
import { type KeyboardEvent, useState } from 'react';

const COUNTER_FROM = 1800;

export interface NoteItemProps {
  note: Note;
  /** `root` opens a discussion (level badge, level editable); `reply` answers it. */
  variant: 'root' | 'reply';
  viewer: NoteViewer;
  /** Named in the level picker. */
  companyName: string;
  /** The replies under a root note, for the deletion confirmation. */
  replyCount?: number;
  /** Saves an edit; reject to show the failure and keep the form open. */
  onUpdate: (input: UpdateNoteInput) => Promise<unknown>;
  /** Deletes the note (and, for a root, its replies). */
  onDelete: () => Promise<unknown>;
  /** The clock of the relative date; tests pin it. */
  now?: Date;
  className?: string;
}

/**
 * One message of a discussion (planche 09): author, short date, level badge on
 * a root, « modifiée », and for its author a « ⋯ » menu to edit in place or
 * delete. Deleting a root that has replies asks first.
 */
export function NoteItem({
  note,
  variant,
  viewer,
  companyName,
  replyCount = 0,
  onUpdate,
  onDelete,
  now,
  className,
}: NoteItemProps) {
  const isAuthor = note.author?.id === viewer.id;
  const isRoot = variant === 'root';
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.body);
  const [draftLevel, setDraftLevel] = useState(note.visibility);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const needsConfirmation = isRoot && replyCount > 0;
  const trimmed = draft.trim();
  const overLimit = draft.length > NOTE_BODY_MAX;
  const canSave = trimmed.length > 0 && !overLimit && !saving;

  function startEditing() {
    setDraft(note.body);
    setDraftLevel(note.visibility);
    setError(null);
    setEditing(true);
  }

  function stopEditing() {
    setEditing(false);
    setError(null);
  }

  async function save() {
    if (!canSave) return;
    const input: UpdateNoteInput = {
      ...(trimmed !== note.body ? { body: trimmed } : {}),
      ...(isRoot && draftLevel !== note.visibility ? { visibility: draftLevel } : {}),
    };
    if (input.body === undefined && input.visibility === undefined) {
      stopEditing();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onUpdate(input);
      setEditing(false);
    } catch (failure) {
      setError(
        noteErrorMessage(failure, "La modification n'a pas pu être enregistrée. Réessayez."),
      );
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    setDeleting(true);
    setError(null);
    try {
      await onDelete();
      setConfirmOpen(false);
    } catch (failure) {
      setError(noteErrorMessage(failure, "La suppression n'a pas abouti. Réessayez."));
    } finally {
      setDeleting(false);
    }
  }

  function handleEditKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      void save();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      stopEditing();
    }
  }

  const confirmTitle =
    replyCount === 1
      ? 'Supprimer cette note et sa réponse ?'
      : `Supprimer cette note et ses ${replyCount} réponses ?`;

  return (
    <div className={cn('text-small leading-snug', className)}>
      <div className="mb-1 flex items-center gap-1 text-caption">
        <b className="font-semibold">{authorName(note.author)}</b>
        <time
          dateTime={note.createdAt}
          title={formatNoteDateTime(note.createdAt)}
          className="text-gray-500"
        >
          {formatNoteDate(note.createdAt, now)}
        </time>
        {isRoot && <NoteLevelBadge visibility={note.visibility} />}
        {note.editedAt && (
          <span title={formatNoteDateTime(note.editedAt)} className="text-gray-500 italic">
            modifiée
          </span>
        )}
        {isAuthor && !editing && (
          <div className="ml-auto">
            <ActionMenu
              trigger={
                <Button
                  size="icon"
                  variant="ghost"
                  icon="more_horiz"
                  aria-label="Actions de la note"
                  disabled={deleting}
                />
              }
              items={[
                { label: 'Modifier', icon: 'edit', onSelect: startEditing },
                {
                  label: 'Supprimer',
                  icon: 'delete',
                  destructive: true,
                  onSelect: needsConfirmation ? () => setConfirmOpen(true) : () => void remove(),
                },
              ]}
            />
          </div>
        )}
      </div>

      {editing ? (
        <div className="flex flex-col gap-2">
          <TextArea
            autoFocus
            label={<span>Modifier la note</span>}
            rows={3}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={handleEditKeyDown}
            status={overLimit ? 'error' : 'default'}
            readOnly={saving}
          />
          <div className="flex items-center gap-2">
            {isRoot && (
              <NoteLevelPicker
                value={draftLevel}
                onChange={setDraftLevel}
                role={viewer.role}
                companyName={companyName}
              />
            )}
            {draft.length >= COUNTER_FROM && (
              <span
                className={cn(
                  'text-micro',
                  overLimit ? 'font-semibold text-rouge-deep' : 'text-gray-600',
                )}
              >
                {draft.length} / {NOTE_BODY_MAX}
              </span>
            )}
            <div className="ml-auto flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={stopEditing}
                disabled={saving}
              >
                Annuler
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={!canSave}
                loading={saving}
                loadingLabel="Enregistrement…"
                onClick={() => void save()}
              >
                Enregistrer
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <p className="whitespace-pre-wrap break-words">{note.body}</p>
      )}

      {error && !confirmOpen && (
        <p role="alert" className="mt-1 font-semibold text-caption text-rouge-deep">
          {error}
        </p>
      )}

      {needsConfirmation && (
        <ConfirmDialog
          open={confirmOpen}
          title={confirmTitle}
          description="La discussion entière sera supprimée. Cette action est irréversible."
          confirmLabel="Supprimer"
          pendingLabel="Suppression…"
          confirmIcon="delete"
          confirmVariant="destructive"
          isPending={deleting}
          errorMessage={confirmOpen ? error : null}
          onConfirm={() => void remove()}
          onCancel={() => {
            setConfirmOpen(false);
            setError(null);
          }}
        />
      )}
    </div>
  );
}
